import { Component, Input, OnChanges, OnInit, SimpleChanges, computed, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AddHutangRequestComponent } from 'src/app/AddThings/add-hutang-request/add-hutang-request.component';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { ALERT_TYPE } from 'src/app/constants';
import { BatchPaymentComponent } from 'src/app/LoanPayment/batch-payment/batch-payment.component';
import { LegacyEpfSettlementComponent } from 'src/app/Loans/legacy-epf-settlement/legacy-epf-settlement.component';
import { LoanDetailsComponent } from 'src/app/Loans/loan-details/loan-details.component';
import { MarkLegacyEpfsComponent } from 'src/app/Loans/mark-legacy-epfs/mark-legacy-epfs.component';
import { AddReserveGroupComponent } from 'src/app/Reserve/add-reserve-group/add-reserve-group.component';
import { ConfirmationDialogComponent } from 'src/app/Shared/confirmation-dialog/confirmation-dialog.component';
import { Enums } from 'src/app/Shared/enums';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { SharedLoanListComponent } from 'src/app/SharedLoan/shared-loan-list/shared-loan-list.component';
import { SharedLoanComponent } from 'src/app/SharedLoan/shared-loan/shared-loan.component';
import { Commission } from 'src/model/commission.model';
import { Customer } from 'src/model/customer.model';
import { EPF } from 'src/model/epf.model';
import { FormJ } from 'src/model/formJ.model';
import { Loan } from 'src/model/loan.model';
import { Profile } from 'src/model/profile.model';
import { BusinessService } from 'src/services/business.service';
import { FileService } from 'src/services/file.service';
import { FormJService } from 'src/services/form-j.service';
import { GetProfileLoansOptions, LoanService } from 'src/services/loan.service';
import { AddLoanComponent } from '../../AddThings/add-loan/add-loan.component';

@Component({
	selector: 'app-customer-loan-card',
	templateUrl: './customer-loan-card.component.html',
	styleUrls: ['./customer-loan-card.component.scss'],
	standalone: false
})
export class CustomerLoanCardComponent implements OnInit, OnChanges {
	private activeModal = inject(NgbActiveModal);
	private restapi = inject(RestApiService);
	private constructApi = inject(ConstructAPI);
	protected enums = inject(Enums);
	protected sharedService = inject(SharedService);
	private router = inject(Router);
	private fileService = inject(FileService);
	private loanService = inject(LoanService);
	private formJService = inject(FormJService);
	private businessService = inject(BusinessService);

	viewCustomer = input.required<Customer>();
	selectedProfile = input.required<Profile>();
	epfDetails = input<EPF>();
	@Input() isModal = false;

	today = signal<Date>(new Date());

	isBranchStaff = signal<boolean>(this.sharedService.isRoles([this.enums.BRANCH_MANAGER, this.enums.BOOKKEEPING_STAFF, this.enums.STAFF]));
	isManagingUser = signal<boolean>(this.sharedService.isRoles([this.enums.ADMIN, this.enums.BOSS]));

	customerStatus = computed<number>(() => this.sharedService.processStatusCode(this.selectedProfile().Status));

	allLoanList = signal<Loan[]>([]);
	filteredLoanList = computed<Loan[]>(() => {
		if (this.isHidingSettled()) return this.customerLoanList().filter((loan: Loan) => loan.Status !== this.enums.SETTLED_LOAN);
		else return this.customerLoanList();
	});
	loanList = computed(() => {
		const all = this.allLoanList();
		return all.reduce(
			(acc, current) => {
				const epf = current.Scheme === 'EPF' || current.IsEPF === true;
				const notSettled = current.Status !== this.enums.SETTLED_LOAN;
				const notCP = current.Status !== this.enums.CP_LOAN;

				if (epf) {
					acc.allEpf.push(current);
					if (notSettled && notCP) acc.allActiveEpf.push(current);
				}

				if (this.isBranchStaff()) {
					if (current.CompanyId !== this.viewCustomer().CompanyId) acc.shared.push(current);
					else {
						acc.nonShared.push(current);
						if (notSettled) acc.active.push(current);
						if (current.IsEPF == true && current.LegacySettlement == undefined) acc.legacyNoSettlement.push(current);
						if (epf) {
							acc.epf.push(current);
							if (notSettled && notCP) acc.activeEpf.push(current);
						}
					}
				} else {
					if (notSettled) acc.active.push(current);
					if (current.IsEPF == true && current.LegacySettlement == undefined) acc.legacyNoSettlement.push(current);
				}
				return acc;
			},
			{
				active: [] as Loan[],
				epf: [] as Loan[],
				activeEpf: [] as Loan[],
				allEpf: [] as Loan[],
				allActiveEpf: [] as Loan[],
				legacyNoSettlement: [] as Loan[],
				shared: [] as Loan[],
				nonShared: [] as Loan[]
			}
		);
	});
	customerLoanList = computed<Loan[]>(() => (this.isBranchStaff() ? this.loanList().nonShared : this.allLoanList()));
	allEpfLoanList = computed<Loan[]>(() => this.loanList().allEpf);
	allActiveEpfLoanList = computed<Loan[]>(() => this.loanList().allActiveEpf);
	legacyNoSettlementLoanList = computed<Loan[]>(() => this.loanList().legacyNoSettlement);
	sharedLoanList = computed<Loan[]>(() => this.loanList().shared.sort((a, b) => a.ID - b.ID));
	activeLoanList = computed<Loan[]>(() => this.loanList().active);
	epfLoanList = computed<Loan[]>(() => (this.isBranchStaff() ? this.loanList().epf : this.loanList().allEpf));
	activeEpfLoanList = computed<Loan[]>(() => (this.isBranchStaff() ? this.loanList().activeEpf : this.loanList().allActiveEpf));

	epfTotalPrincipal = computed(() => {
		const list = this.loanList().activeEpf;
		return list.reduce((acc, current) => acc + current.Principal, 0);
	});
	allLoanSettlementBalance = computed<number>(() => this.sharedService.roundTo2Dec(this.sharedService.calculateTotalSettlementBalance(this.allLoanList())));
	customerLoanSettlementBalance = computed<number>(() => this.sharedService.roundTo2Dec(this.sharedService.calculateTotalSettlementBalance(this.customerLoanList())));
	allLoanSettlementRatio = computed<number>(() => (this.sharedService.calculateTotalSettlementBalance(this.allLoanList()) / this.epfDetails().Amount) * 100);
	totalEarning = computed<number>(() => {
		return this.customerLoanList().reduce((acc, current) => {
			acc +=
				(current.Scheme === 'A' ? current.PrincipalPaid : current.Repayment) +
				current.InterestCharge +
				current.MonthlyInterest +
				current.ArrearPaid +
				current.ProcessingCharge -
				current.Principal +
				current.Discount;
			return acc;
		}, 0);
	});

	isHidingSettled = signal<boolean>(false);
	allowAddLoan = computed<boolean>(() => !this.isSeniorRestricted());
	allowAddHutang = computed<boolean>(() => !(this.epfLoanList().length > 0));
	allowAddEpf = computed<boolean>(() =>
		this.sharedService.checkIsAllowEPF(this.viewCustomer().DOB, this.epfDetails(), this.selectedProfile().ID, this.selectedProfile().SharedProfileId)
	);
	hasSharedLoan = computed<boolean>(() => this.sharedService.isBranchManager() && this.sharedLoanList().length > 0);
	isSeniorRestricted = computed<boolean>(() => this.sharedService.checkIsSeniorRestrictedCustomer(new Date(this.viewCustomer().DOB)));
	canPrintSettlementLetter = computed<boolean>(
		() =>
			!this.sharedService.isStaff() &&
			this.epfDetails() !== undefined &&
			this.epfDetails().Amount !== undefined &&
			(this.selectedProfile().SharedProfileId === this.selectedProfile().ID || this.selectedProfile().SharedProfileId === undefined)
	);
	canPrintBankRedemptionLetter = computed<boolean>(() => this.isManagingUser() && this.allLoanSettlementBalance() > 0);

	latestFormJ = signal<FormJ>(undefined);

	commissionList: Commission[] = [];

	ngOnInit() {
		if (this.isModal == true) this.getCustomerLoanInfo();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.selectedProfile) {
			this.getCustomerLoanInfo();
		}
	}

	openBatchPaymentModal() {
		const activeLoan = this.loanList().active;
		const modalRef = this.sharedService.openModal(BatchPaymentComponent, 'largeModal');
		modalRef.componentInstance.loanList = activeLoan;
		modalRef.componentInstance.selectedCustomer = this.viewCustomer();
		modalRef.componentInstance.selectedProfile = this.selectedProfile();
		modalRef.componentInstance.sharedProfileId = this.selectedProfile().SharedProfileId;
	}

	openAddReserveGroup() {
		const modalRef = this.sharedService.openModal(AddReserveGroupComponent, 'largeModal');
		modalRef.componentInstance.sharedProfileId = this.selectedProfile().SharedProfileId;
		modalRef.componentInstance.selectedProfileId = this.selectedProfile().ID;
		const activeLoan = this.loanList().active;
		modalRef.componentInstance.loanList = activeLoan;
		modalRef.componentInstance.selectedCustomer = this.viewCustomer();
		modalRef.componentInstance.selectedProfile = this.selectedProfile();
	}

	checkIfLegacySettlementCompleted() {
		const legacyNoSettlementLoanList = this.legacyNoSettlementLoanList();

		if (legacyNoSettlementLoanList.length > 0) {
			const modalRef = this.sharedService.openModal(LegacyEpfSettlementComponent, 'regularModal');
			modalRef.componentInstance.loanList = legacyNoSettlementLoanList;

			//if completed
			modalRef.componentInstance.passEntry.subscribe(entry => {
				const success = entry;

				//append added loan to loan list
				if (success) this.openAddLoanModal();
			});
		} else this.openAddLoanModal();
	}

	openHutangLoanModal() {
		const modalRef = this.sharedService.openModal(AddHutangRequestComponent, 'largeModal');
		modalRef.componentInstance.selectedCustomer = this.viewCustomer();
		modalRef.componentInstance.selectedProfile = this.selectedProfile();
	}

	async openAddSharedLoanModal() {
		const modalRef = this.sharedService.openModal(SharedLoanComponent, 'regularModal');
		modalRef.componentInstance.selectedCustomer = this.viewCustomer();
		modalRef.componentInstance.selectedProfile = this.selectedProfile();

		if (this.epfDetails() != undefined) modalRef.componentInstance.epfDetails = this.epfDetails();
		else {
			const epf = new EPF();
			modalRef.componentInstance.epfDetails = epf;
		}

		modalRef.componentInstance.isEPF = true;
		//get current loans and filter EPF loans to check if it's an extra customer
		//if is main shared company need to add shared loan, need to add the previous loans of others shared companies
		// need to filter share loan to make sure it's not settled / cp loan
		const filtered = this.allActiveEpfLoanList();

		modalRef.componentInstance.isEPFExtra = filtered != undefined && filtered.length > 0 ? true : false;
		modalRef.componentInstance.previousEPFLoans = filtered;
		modalRef.componentInstance.selectedProfile = this.selectedProfile();

		modalRef.componentInstance.needRestrictedRequest = this.isSeniorRestricted();
		modalRef.componentInstance.allowEPF = this.allowAddEpf();
	}

	openAddLoanModal() {
		const modalRef = this.sharedService.openModal(AddLoanComponent, 'regularModal');
		modalRef.componentInstance.selectedCustomer = this.viewCustomer();
		modalRef.componentInstance.selectedProfile = this.selectedProfile();

		if (this.epfDetails() != undefined) modalRef.componentInstance.epfDetails = this.epfDetails();
		else {
			modalRef.componentInstance.epfDetails = new EPF();
		}

		//get current loans and filter EPF loans to check if it's an extra customer
		const filtered = this.loanList().activeEpf;
		modalRef.componentInstance.isEPFExtra = filtered != undefined && filtered.length > 0 ? true : false;
		modalRef.componentInstance.previousEPFLoans = filtered;

		modalRef.componentInstance.needRestrictedRequest = this.isSeniorRestricted();
		// modalRef.componentInstance.allowEPF = this.sharedService.checkIsAllowEPF(this.viewCustomer().DOB, this.epfDetails(), this.selectedProfile().ID, this.selectedProfile().SharedProfileId);
		//get added loan
		modalRef.componentInstance.passEntry.subscribe(entry => {
			const loan = entry;

			//append added loan to loan list
			this.allLoanList.update(prev => [...prev, loan]);
		});

		// modalRef.result.then(() => {
		//   //detect changes
		//   this.cdr.reattach();
		// })
	}

	epfEarlySettlement(monthFromToday: number, includeShared: boolean = false) {
		const today = this.today();
		const date = new Date(today.getFullYear(), today.getMonth() + monthFromToday, today.getDate());
		const list = includeShared ? this.allActiveEpfLoanList() : this.activeEpfLoanList();
		return list.reduce((total, current) => {
			total += monthFromToday === 0 ? current.EarlySettlementAmount : this.sharedService.calculateSettlementToDate(current, date, this.epfDetails().Date);
			return total;
		}, 0);
	}

	async getCustomerLoanInfo() {
		this.allLoanList.set([]);

		const isMainSharedProfile = this.sharedService.checkIsMainShareProfile(this.selectedProfile());
		const o: GetProfileLoansOptions = this.isBranchStaff() ? { includedSharedProfile: isMainSharedProfile } : {};
		const loanList = await this.loanService.getLoanOfCustomerProfile(this.selectedProfile().ID, o);

		loanList.sort((a, b) => b.ID - a.ID);

		//both branch user and managing user need to check early settlement amount
		//this is also to be shown in loan card -> balance details
		if (this.isBranchStaff() || this.isManagingUser()) {
			if (this.epfDetails() != undefined)
				loanList.map(
					loan => (loan.EarlySettlementAmount = loan.IsEPF == true ? loan.LegacySettlement : this.sharedService.calculateSettlementToDate(loan, new Date(), this.epfDetails().Date))
				);
		}

		if (!this.sharedService.isStaff() && !this.sharedService.isFinance()) {
			const [loansWithFiles, loansWithPendingComm, formJs] = await Promise.all([
				this.loanService.getCustomerLoansWithFiles(this.viewCustomer().ID),
				this.loanService.getAllPendingCommLoanIds(this.selectedProfile().ID),
				this.formJService.getCustomerFormJs(this.viewCustomer().ID, { hasLoanGroup: true })
			]);
			this.latestFormJ.set(formJs.sort((a, b) => (b.DateStamped?.getTime() || 0) - (a.DateStamped?.getTime() || 0))[0]);
			const formJMap = new Map(formJs.map(a => [a.LoanGroupId, a]));
			loanList.map(l => {
				const formJ = formJMap.get(l.LoanGroupId);
				l.FormJ = formJ;
				l.HasStampedFormJ = formJ?.DateStamped !== undefined;
				l.LoanDocExisted = loansWithFiles?.includes(l.ID);
				l.IsPendingCommission = loansWithPendingComm?.includes(l.ID);
			});
			this.isHidingSettled.set(true);
		}

		this.allLoanList.set(loanList);
	}

	sendProfileLoansToCP() {
		const xhr = this.restapi.putRequest(this.constructApi.getSendProfileLoansToCP(this.selectedProfile().ID), {});
		xhr.onreadystatechange = () => {
			if (xhr.readyState == 4) {
				if (xhr.status == 200) this.refresh();
				else this.sharedService.openErrorMessage(xhr);
			}
		};
	}

	sendLoansToLBD() {
		const xhr = this.restapi.putRequest(this.constructApi.getSendProfileLoansToLBD(this.selectedProfile().ID));
		xhr.onreadystatechange = () => {
			if (xhr.readyState == 4) {
				if (xhr.status == 200) this.refresh();
				else this.sharedService.openErrorMessage(xhr);
			}
		};
	}

	confirmBeforeSendCP() {
		const proceed = false;
		const modalRef = this.sharedService.openModal(ConfirmationDialogComponent);
		modalRef.componentInstance.message = 'You sure you want to send this customer to CP?';

		modalRef.result.then(confirm => {
			if (confirm) this.sendProfileLoansToCP();
		});
	}

	async confirmBeforeDownloadCPFiles() {
		const modalRef = this.sharedService.openModal(ConfirmationDialogComponent);
		modalRef.componentInstance.message = "You sure you want to download this customer's CP archive?";

		//get confirmation

		modalRef.result.then(async confirm => {
			if (confirm) {
				const url = await this.constructApi.getCustomerFilesArchive(this.viewCustomer().ID, true);
				const file = await this.fileService.getFileByUrl(url);
				this.fileService.downloadFile(file);
			}
		});
	}

	confirmBeforeSendLegal() {
		const proceed = false;
		const modalRef = this.sharedService.openModal(ConfirmationDialogComponent);
		modalRef.componentInstance.message = 'You sure you want to send this customer to Legal Department?';

		modalRef.result.then(confirm => {
			if (confirm) this.sendLoansToLBD();
		});
		return proceed;
	}

	refresh(): void {
		window.location.reload();
	}

	calculateTotalSettlement(loanList: Loan[]) {
		let totalSettlement = 0;
		for (let i = 0; i < loanList.length; i++) {
			if (loanList[i].Scheme == 'A') {
				//if is LEGACY A EPF
				if (loanList[i].IsEPF == true) totalSettlement += loanList[i].LegacySettlement || 0;
				//if is normal A
				else totalSettlement += loanList[i].Principal;
			} else if (loanList[i].Scheme == 'HUTANG') {
				totalSettlement += loanList[i].Principal;
			} else {
				//if settlement charges != undefined, this case only can be true when it's epf loan
				// add principal + interest + all settlement charges left- Repayment made
				if (loanList[i].HasSettlementChargesObj)
					totalSettlement +=
						loanList[i].Principal +
						loanList[i].Principal * (loanList[i].InterestRate / 100) * loanList[i].Term +
						((loanList[i].SettlementCharges.Extras || 0) +
							(loanList[i].SettlementCharges.Stamp || 0) +
							(loanList[i].SettlementCharges.ServiceCharge || 0) +
							(loanList[i].SettlementCharges.HandlingCharge || 0));
				//if there's no settlement charges, just add on all the principal + interest + legacy defined settlement charges values - Repayment made
				else
					totalSettlement +=
						loanList[i].Principal +
						loanList[i].Principal * (loanList[i].InterestRate / 100) * loanList[i].Term +
						((loanList[i].SettlementExtra || 0) + (loanList[i].SettlementStamp || 0) + (loanList[i].SettlementServiceCharge || 0));
			}
		}

		return totalSettlement;
	}

	calculateTotalIncludeInterest(loan: Loan) {
		return this.sharedService.roundToDec((loan.InterestRate / 100) * loan.Principal * loan.Term + loan.Principal, 2);
	}

	openLoanDetailsModal(loan: Loan) {
		const modalRef = this.sharedService.openModal(LoanDetailsComponent, 'largeModal');
		modalRef.componentInstance.selectedLoan = loan;
	}

	openMarkLegacyEPF() {
		const nonEpfLoanList = this.customerLoanList().filter((loan: Loan) => loan.Scheme != 'EPF' && loan.Scheme != 'HUTANG' && loan.IsEPF != true);

		if (nonEpfLoanList.length == 0) {
			this.sharedService.openAlert('You have no valid loan can be added to EPF', ALERT_TYPE.DANGER);
		} else {
			const modalRef = this.sharedService.openModal(MarkLegacyEpfsComponent, 'largeModal');
			modalRef.componentInstance.epfDetails = this.epfDetails();

			modalRef.componentInstance.loanList = nonEpfLoanList;

			modalRef.componentInstance.selectedCustomer = this.viewCustomer();
			modalRef.componentInstance.selectedProfileID = this.selectedProfile().ID;
		}
	}

	reset() {
		this.activeModal.close();
	}

	openSharedLoanListModal() {
		if (this.sharedLoanList().length == 0) {
			this.sharedService.openAlert('No shared company open loan yet', ALERT_TYPE.DANGER);
		} else {
			const modalRef = this.sharedService.openModal(SharedLoanListComponent, 'largeModal');
			modalRef.componentInstance.sharedLoanList = this.sharedLoanList();
			modalRef.componentInstance.viewCustomer = this.viewCustomer();
			modalRef.componentInstance.selectedProfile = this.selectedProfile();
		}
	}

	async goToSettlementLetter() {
		// filter own epf loan and + shared loan
		const epfLoan = this.allActiveEpfLoanList();
		const settlement = epfLoan.length == 0 ? 0 : await this.sharedService.calculateTotalSettlementBalance(epfLoan);
		const simpan = epfLoan.length == 0 ? 0 : this.selectedProfile().Reserve;
		const lastExtraDate =
			epfLoan.length == 0 ?
				this.today()
			:	await new Date(
					Math.max.apply(
						null,
						epfLoan.map(e => new Date(e.DateCreated).getTime())
					)
				);
		this.router.navigate(['/settlementBreakdown', this.viewCustomer().CompanyId, this.viewCustomer().ID], {
			state: { settlement: settlement, simpan: simpan, latestDate: lastExtraDate }
		});
	}

	async goToBankRedemptionLetter() {
		let licenseId = -1;
		if (this.latestFormJ() === undefined) {
			try {
				// *if no active license, cannot print letter
				const license = await this.businessService.getLatestLicenseByCompanyId(this.viewCustomer().CompanyId);
				licenseId = license.Id;
			} catch (err) {
				if (err.status === 404) {
					this.sharedService.openAlert('No active business license, please contact admin to add or renew business license!', ALERT_TYPE.DANGER);
					return;
				}
			}
		} else licenseId = this.latestFormJ().LicenseId;
		this.router.navigate(['/bank-redemption-letter', this.viewCustomer().ID], {
			state: {
				settlement: this.allEpfLoanList().length > 0 ? this.allLoanSettlementBalance() : 0,
				customerName: this.viewCustomer().Name,
				customerIc: this.viewCustomer().ICNumberWithDash,
				licenseId: licenseId
			}
		});
	}
}
